/* global ResizeObserver */
/**
 * WordPress dependencies.
 */
import {
	store,
	getContext,
	getElement,
	withScope,
} from '@wordpress/interactivity';

/**
 * Helper to find the grid element from any child.
 * @param {HTMLElement} el
 */
const getGridEl = ( el ) => {
	const wrapper = el.closest( '.horizontal-scroll-grid-wrapper' );
	return wrapper
		? wrapper.querySelector( '.is-style-horizontal-scroll-grid' )
		: null;
};

const { state } = store( 'vijayan/interactive-scroll-grid', {
	state: {
		get isFirst() {
			const { currentIndex } = getContext();
			return currentIndex === 0;
		},
		get isLast() {
			const { totalDots } = state;

			// If there are no dots, it means there's only one page.
			if ( totalDots.length === 0 ) {
				return true;
			}

			const { currentIndex } = getContext();
			// Check if we are at the final page.
			return currentIndex >= totalDots.length - 1;
		},
		get isDotActive() {
			const { dotIndex, currentIndex } = getContext();
			return currentIndex === dotIndex;
		},
		get totalDots() {
			const { gridWidth, gridScrollWidth } = getContext();

			if ( ! gridWidth || gridWidth === 0 ) {
				return [];
			}

			// Calculate the maximum physical distance the grid can scroll
			const maxScrollLeft = gridScrollWidth - gridWidth;

			// If the content is smaller than or equal to the container, no dots are needed
			if ( maxScrollLeft <= 0 ) {
				return [];
			}

			// Calculate the highest reachable index using the same rounding logic
			const maxIndex = Math.round( maxScrollLeft / gridWidth );
			const pages = maxIndex + 1;

			return pages > 1
				? Array.from( { length: pages }, ( _, i ) => i )
				: [];
		},
	},
	actions: {
		next: () => {
			const grid = getGridEl( getElement().ref );
			if ( grid && ! state.isLast ) {
				grid.scrollBy( {
					left: grid.clientWidth,
					behavior: 'smooth',
				} );
			}
		},
		prev: () => {
			const grid = getGridEl( getElement().ref );
			if ( grid && ! state.isFirst ) {
				grid.scrollBy( {
					left: -grid.clientWidth,
					behavior: 'smooth',
				} );
			}
		},
		goToIndex: () => {
			const grid = getGridEl( getElement().ref );
			const { dotIndex } = getContext();

			if ( grid && typeof dotIndex !== 'undefined' ) {
				grid.scrollTo( {
					left: dotIndex * grid.clientWidth,
					behavior: 'smooth',
				} );
			}
		},
	},
	callbacks: {
		initGrid: () => {
			const { ref } = getElement();
			const grid = getGridEl( ref );
			if ( ! grid ) {
				return;
			}

			const context = getContext();

			// Initialize reactive dimensions in context.
			context.gridWidth = grid.clientWidth;
			context.gridScrollWidth = grid.scrollWidth;

			// Keep them in sync on resize.
			const observer = new ResizeObserver(
				withScope( ( entries ) => {
					for ( const entry of entries ) {
						context.gridWidth = entry.target.clientWidth;
						context.gridScrollWidth = entry.target.scrollWidth;
					}
				} )
			);
			observer.observe( grid );

			// Sync index on scroll (handles touch drag, trackpad, and button clicks)
			grid.addEventListener(
				'scroll',
				withScope( () => {
					const width = context.gridWidth || grid.clientWidth;
					if ( ! width ) {
						return;
					}

					const newIndex = Math.round( grid.scrollLeft / width );
					if ( context.currentIndex !== newIndex ) {
						context.currentIndex = newIndex;
					}
				} ),
				{ passive: true }
			);
		},
	},
} );
